import React, { useEffect, useRef, useState } from "react";
import { api, type SfxSound } from "../api.js";
import { Icon } from "./Icon.js";

let cache: SfxSound[] | null = null;
let pending: Promise<SfxSound[]> | null = null;
const listeners = new Set<(s: SfxSound[]) => void>();

/** The sound library, fetched once per page and shared by every picker and the preview. */
export function useSfxLibrary(): SfxSound[] {
  const [sounds, setSounds] = useState<SfxSound[]>(cache ?? []);
  useEffect(() => {
    listeners.add(setSounds);
    if (cache) setSounds(cache);
    else {
      pending ??= reloadSfx();
    }
    return () => { listeners.delete(setSounds); };
  }, []);
  return sounds;
}

/** Fetches the library again (after an upload or a removal) and tells every picker. */
export const reloadSfx = (): Promise<SfxSound[]> => api.sfx().then((m) => { cache = m.sounds.filter((s) => s.available); listeners.forEach((l) => l(cache!)); return cache; }).catch(() => cache ?? []);

/** id -> url for the reel props. */
export const sfxUrlMap = (sounds: SfxSound[]): Record<string, string> => Object.fromEntries(sounds.map((s) => [s.id, `/assets/sfx/${s.file}`]));

const GROUP_LABEL: Record<string, string> = { transition: "Whooshes and transitions", hit: "Hits", ui: "Small sounds", texture: "Textures", uploaded: "My sounds" };

/** Choose one library sound (or none) for an event, with a play button to hear it. */
export const SfxPick: React.FC<{ label: string; value: string | null; onChange: (id: string | null) => void; hint?: string }> = ({ label, value, onChange, hint }) => {
  const sounds = useSfxLibrary();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const current = sounds.find((s) => s.id === value);
  const play = () => {
    if (!current) return;
    audioRef.current?.pause();
    const a = new Audio(`/assets/sfx/${current.file}`); a.volume = 0.7; a.play().catch(() => {}); a.onended = () => setPlaying(false);
    audioRef.current = a; setPlaying(true);
  };
  useEffect(() => () => { audioRef.current?.pause(); }, []);
  const [busy, setBusy] = useState(false);
  const upload = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try { const r = await api.uploadSfx(f); await reloadSfx(); onChange(r.sound.id); } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };
  const remove = async () => { if (!current || current.group !== "uploaded") return; await api.deleteSfx(current.id); onChange(null); await reloadSfx(); };
  const groups = Array.from(new Set(sounds.map((s) => s.group)));
  return (
    <div className="col" style={{ gap: 4 }}>
      <div className="row" style={{ justifyContent: "space-between", fontSize: 12, gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <div className="row" style={{ gap: 4 }}>
          <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} style={{ minWidth: 140 }} title={hint}>
            <option value="">None</option>
            {groups.map((g) => <optgroup key={g} label={GROUP_LABEL[g] ?? g}>{sounds.filter((s) => s.group === g).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}</optgroup>)}
          </select>
          <button className="btn sm" disabled={!current} onClick={play} title="Hear it"><Icon name={playing ? "pause" : "play"} size={12} sw={0} color="currentColor" /></button>
          <label className="btn sm" title="Bring in your own sound (mp3, wav, m4a); it is trimmed to 6 s and levelled like the rest" style={{ cursor: "pointer" }}><Icon name="plus" size={12} />{busy ? "…" : ""}<input type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0])} /></label>
          {current?.group === "uploaded" ? <button className="btn sm" onClick={remove} title="Remove this uploaded sound"><Icon name="trash" size={12} /></button> : null}
        </div>
      </div>
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
};
